import { asyncify, each } from 'async';
import { StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  activateSpaceMembershipTierInventory as restActivateSpaceMembershipTierInventory,
  changeUserSpaceMembership as restChangeSpaceMembership,
  deleteAlbum as restDeleteAlbum,
  getAlbum as restGetAlbum,
  getAlbums as restGetAlbums,
  getRecentAlbums as restGetRecentAlbums,
  getSpace as restGetSpace,
  getSpaceMembershipTierInventories as restGetSpaceMembershipTierInventories,
  getSpaceMembershipTierSubscriptions as restGetSpaceMembershipTierSubscriptions,
  getSpacePerformanceReport as restGetSpacePerformanceReport,
  getSpacePerformanceReportGame as restGetSpacePerformanceReportGame,
  getSpaces as restGetSpaces,
  joinTeamForLeaderboard as restJoinTeamForLeaderboard,
  searchSpaces as restSearchSpaces,
  seeSpace as restSeeSpace,
  updateAlbum as restUpdateAlbum,
} from '../../api/Rest/space';
import { SpaceMembershipRole } from '../../constants/space';
import { Uuid } from '../../models/Base';
import { jsonToGame } from '../../models/GameStore';
import { Cursor } from '../../models/Repository';
import {
  Album,
  AlbumListType,
  AlbumRepository,
  GameRepository,
  Leaderboard,
  MultiplayerGame,
  MultiplayerGameRepository,
  MultiplayerGameSession,
  MultiplayerGameSessionRepository,
  PerformanceGame,
  PerformanceGameRepository,
  Space,
  SpaceLeaderboardRepository,
  SpaceListType,
  SpaceMembership,
  SpaceMembershipRepository,
  SpaceMembershipState,
  SpaceMembershipTier,
  SpaceMembershipTierInventory,
  SpaceMembershipTierInventoryJson,
  SpaceMembershipTierInventoryListType,
  SpaceMembershipTierInventoryRepository,
  SpaceMembershipTierJson,
  SpaceMembershipTierMapType,
  SpaceMembershipTierRepository,
  SpaceMembershipTierSubscription,
  SpaceMembershipTierSubscriptionJson,
  SpaceMembershipTierSubscriptionListType,
  SpaceMembershipTierSubscriptionRepository,
  SpaceRepository,
  SpaceStoreAction,
  SpaceStoreState,
  SpaceStoreStateJson,
  SpaceTeam,
  SpaceTeamRepository,
} from '../../models/SpaceStore';
import {
  createStoreListFieldHook,
  createStoreMapFieldHook,
  FetchActionResult,
  StorageValue,
  StoreLifecycle,
  StoreLifecycleAction,
} from '../../models/Store';
import {
  Profile,
  ProfileMapType,
  ProfileWithMemberships,
  WebBoosterType,
} from '../../models/UserStore';
import { dateNumberReviver } from '../../utils/json';
import { useRecordStore } from '../RecordStore';
import { useUserStore } from '../UserStore';

import * as SpaceStore from './state';

type Store = StoreLifecycleAction & SpaceStoreState & SpaceStoreAction;

const lifecycle = new StoreLifecycle('SpaceStore');

const initializer: StateCreator<Store> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      const state = {
        spaces: new SpaceRepository(),
        spaceMembershipTierInventories:
          new SpaceMembershipTierInventoryRepository(),
        spaceMembershipTierSubscriptions:
          new SpaceMembershipTierSubscriptionRepository(),
        spaceMembershipTiers: new SpaceMembershipTierRepository(),
        albums: new AlbumRepository(),
        spaceMemberships: new SpaceMembershipRepository(),
        teams: new SpaceTeamRepository(),
        leaderboards: new SpaceLeaderboardRepository(),
        games: new GameRepository(),
        multiplayerGames: new MultiplayerGameRepository(),
        multiplayerGameSessions: new MultiplayerGameSessionRepository(),
        performanceGames: new PerformanceGameRepository(),
      };
      state.albums.onElementPrune = onAlbumPrune;
      state.spaceMembershipTierInventories.onElementPrune =
        onTierInventoryPrune;
      set(state);
    },
    () => set((state) => ({ ...state })),
  );

  const getSpace: SpaceStoreAction['getSpace'] = (classroomId) =>
    SpaceStore.getSpace(get(), classroomId);

  const getSpaces: SpaceStoreAction['getSpaces'] = (fieldName) =>
    SpaceStore.getSpaces(get(), fieldName);

  const resetSpacesList: SpaceStoreAction['resetSpacesList'] = (fieldName) =>
    SpaceStore.setSpaces(get(), fieldName, ...[]);

  const handleFetchSpacesResult: SpaceStoreAction['handleFetchSpacesResult'] = (
    fieldNames,
    oldCursor,
    classrooms,
    profiles,
    newCursor,
  ) => {
    set((state) => {
      const spaces = classrooms.map((classroom) => new Space(classroom.group));
      if (oldCursor === undefined) {
        SpaceStore.setSpaces(state, fieldNames, ...spaces);
      } else {
        SpaceStore.pushSpaces(state, fieldNames, ...spaces);
      }
      SpaceStore.setActiveUserMemberships(
        state,
        classrooms.reduce<Record<Uuid, SpaceMembership | null>>(
          (acc, classroom) => {
            acc[classroom.group.classroomId] = classroom.membership
              ? new SpaceMembership(classroom.membership)
              : null;
            return acc;
          },
          {},
        ),
      );

      SpaceStore.setSpaceCursor(state, fieldNames, newCursor);
      return { ...state };
    });

    useUserStore.getState().addProfilesWithMemberships(
      ProfileMapType.SpaceRoot,
      classrooms.reduce<Record<Uuid, ProfileWithMemberships>>(
        (acc, classroom) => {
          // make sure space exists
          const space = getSpace(classroom.group.classroomId);
          if (space) {
            const p = profiles.find(
              ({ profile }) => profile.userId === classroom.rootId,
            );
            if (p) {
              acc[classroom.group.classroomId] = {
                profile: new Profile(p.profile),
                membership: p.membership
                  ? new SpaceMembership(p.membership)
                  : null,
              };
            } else {
              console.warn(
                `missing user profile ${classroom.rootId}, maybe account was deactiviated`,
              );
            }
          }
          return acc;
        },
        {},
      ),
    );

    return { shouldFetchMore: !!newCursor && classrooms.length === 0 };
  };

  const fetchManagingSpaces: SpaceStoreAction['fetchManagingSpaces'] = async (
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetSpaces(
      {
        limit: 20,
        withProfiles: true,
        cursor,
        state: SpaceMembershipState.InGroup,
        role: [
          SpaceMembershipRole.Root,
          SpaceMembershipRole.Admin,
          SpaceMembershipRole.Moderator,
        ],
      },
      options,
    );

    if (ok) {
      return handleFetchSpacesResult(
        SpaceListType.Managing,
        cursor,
        data.classrooms,
        data.profiles,
        data.cursor ?? null,
      );
    }

    return { shouldFetchMore: false };
  };

  const fetchManagingSpacesByUserId: SpaceStoreAction['fetchManagingSpacesByUserId'] =
    async (userId, cursor, options) => {
      const { ok, data } = await restGetSpaces(
        {
          limit: 20,
          withProfiles: true,
          cursor,
          userId: userId,
          state: SpaceMembershipState.InGroup,
          role: [
            SpaceMembershipRole.Root,
            SpaceMembershipRole.Admin,
            SpaceMembershipRole.Moderator,
          ],
        },
        options,
      );

      if (ok) {
        return handleFetchSpacesResult(
          `${SpaceListType.Managing}_${userId}`,
          cursor,
          data.classrooms,
          data.profiles,
          data.cursor ?? null,
        );
      }

      return { shouldFetchMore: false };
    };

  const fetchSpace: SpaceStoreAction['fetchSpace'] = async (
    fieldNames,
    classroomId,
  ) => {
    const space = getSpace(classroomId);
    const membership = getActiveUserMembership(classroomId);
    if (space && membership) {
      // Add existed space
      set((state) => {
        SpaceStore.pushSpaces(state, fieldNames, space);
        SpaceStore.setActiveUserMemberships(state, {
          [space.classroomId]: membership,
        });
        return { ...state };
      });
      return;
    }

    // Fetch from server
    const { ok, data } = await restGetSpace(
      { classroomId },
      { withProfiles: true },
    );
    if (ok) {
      handleFetchSpacesResult(
        fieldNames,
        'cursor',
        [data.classroom],
        data.profiles,
        null,
      );
    }
  };

  const searchSpaces: SpaceStoreAction['searchSpaces'] = async (
    params,
    options,
    // isLog = true,
  ) => {
    const { ok, data } = await restSearchSpaces(
      {
        withProfiles: true,
        ...params,
      },
      options,
    );

    if (ok) {
      handleFetchSpacesResult(
        SpaceListType.SearchResult,
        params?.classroomCursor,
        data.classrooms,
        data.profiles,
        data.classroomCursor ?? null,
      );
      // // create activity log
      // if (params?.key && isLog) {
      //   useSessionStore.getState().createSearchHistory({
      //     action: ActivityLogAction.SearchSpaces,
      //     object: { type: 'key', text: params.key },
      //   });
      // }
    }

    return {
      shouldFetchMore:
        data?.classroomCursor !== undefined && data.classrooms.length === 0,
    };
  };

  const fetchHotSpaces: SpaceStoreAction['fetchHotSpaces'] = async (
    params = { label: 'trending', state: 'available' },
    options,
    // isLog = true,
  ) => {
    const { ok, data } = await restSearchSpaces(
      {
        withProfiles: true,
        ...params,
      },
      options,
    );

    if (ok) {
      handleFetchSpacesResult(
        SpaceListType.Hot,
        params?.classroomCursor,
        data.classrooms,
        data.profiles,
        data.classroomCursor ?? null,
      );

      // // create activity log
      // if (params?.key && isLog) {
      //   useSessionStore.getState().createSearchHistory({
      //     action: ActivityLogAction.SearchSpaces,
      //     object: { type: 'key', text: params.key },
      //   });
      // }
    }

    return {
      shouldFetchMore:
        data?.classroomCursor !== undefined && data.classrooms.length === 0,
    };
  };

  const fetchRecentSpaces: SpaceStoreAction['fetchRecentSpaces'] = async (
    webBoosterType,
    cursor,
    options,
  ) => {
    let label;
    if (webBoosterType === WebBoosterType.Normal) {
      label = '!learning';
    } else if (webBoosterType === WebBoosterType.Bible) {
      label = '!trending';
    }

    const { ok, data } = await restGetSpaces(
      {
        limit: 20,
        type: 'custom',
        withProfiles: true,
        label,
        cursor,
      },
      options,
    );
    // console.log(data);

    if (ok) {
      return handleFetchSpacesResult(
        SpaceListType.Recent,
        cursor,
        data.classrooms,
        data.profiles,
        data.cursor ?? null,
      );
    }

    return {
      shouldFetchMore: false,
    };
  };

  const getTierInventories: SpaceStoreAction['getTierInventories'] = (
    fieldName,
  ) => SpaceStore.getSpaceMembershipTierInventories(get(), fieldName);

  const handleFetchTierInventoriesResult = (
    fieldNames: string | string[],
    oldCursor: Cursor,
    inventories: SpaceMembershipTierInventoryJson[],
    tiers: SpaceMembershipTierJson[],
    newCursor: Cursor,
  ): FetchActionResult => {
    set((state) => {
      const newInventories = inventories.map(
        (i) => new SpaceMembershipTierInventory(i),
      );
      if (oldCursor === undefined) {
        SpaceStore.setSpaceMembershipTierInventories(
          state,
          fieldNames,
          ...newInventories,
        );
      } else {
        SpaceStore.pushSpaceMembershipTierInventories(
          state,
          fieldNames,
          ...newInventories,
        );
      }
      SpaceStore.setSpaceMembershipTierInventoryCursor(
        state,
        fieldNames,
        newCursor,
      );

      // tier
      newInventories.forEach((i) => {
        const tier = tiers.find(
          (t) => t.classroomId === i.classroomId && t.tierId == i.tierId,
        );
        if (tier !== undefined) {
          SpaceStore.addSpaceMemebershipTierToMap(
            state,
            i.classroomId,
            i.tierId,
            new SpaceMembershipTier(tier),
          );
        } else {
          SpaceStore.addSpaceMemebershipTierToMap(
            state,
            i.classroomId,
            i.tierId,
            null,
          );
        }
      });

      return { ...state };
    });
    return { shouldFetchMore: !!newCursor && inventories.length === 0 };
  };

  const fetchTierInventories: SpaceStoreAction['fetchTierInventories'] = async (
    cursor,
    options,
  ) => {
    const fieldName = SpaceMembershipTierInventoryListType.All;
    // Fetch from server
    const { ok, data } = await restGetSpaceMembershipTierInventories(
      { cursor: cursor },
      options,
    );
    if (ok) {
      return handleFetchTierInventoriesResult(
        fieldName,
        cursor,
        data.classroomMembershipTierInventories,
        data.classroomMembershipTiers,
        data.cursor ?? null,
      );
    }
    return { shouldFetchMore: false };
  };

  const activateTierInventory: SpaceStoreAction['activateTierInventory'] =
    async (classroomId, tierId, params, options) => {
      const { ok, data } = await restActivateSpaceMembershipTierInventory(
        { classroomId, tierId },
        params,
        options,
      );
      if (ok) {
        set((state) => {
          state.spaceMembershipTierInventories.set(
            new SpaceMembershipTierInventory(
              data.classroomMembershipTierInventory,
            ),
          );
          return { ...state };
        });
        return true;
      }
      return false;
    };

  const getTierSubscriptions: SpaceStoreAction['getTierSubscriptions'] = (
    fieldName,
  ) => SpaceStore.getSpaceMembershipTierSubscriptions(get(), fieldName);

  const handleFetchTierSubscriptionsResult = (
    fieldNames: string | string[],
    oldCursor: Cursor,
    subscriptions: SpaceMembershipTierSubscriptionJson[],
    tiers: SpaceMembershipTierJson[],
    // classrooms: ClassroomData[],
    newCursor: Cursor,
  ): FetchActionResult => {
    set((state) => {
      const newSubscriptions = subscriptions.map(
        (i) => new SpaceMembershipTierSubscription(i),
      );
      if (oldCursor === undefined) {
        SpaceStore.setSpaceMembershipTierSubscriptions(
          state,
          fieldNames,
          ...newSubscriptions,
        );
      } else {
        SpaceStore.pushSpaceMembershipTierSubscriptions(
          state,
          fieldNames,
          ...newSubscriptions,
        );
      }
      SpaceStore.setSpaceMembershipTierSubscriptionCursor(
        state,
        fieldNames,
        newCursor,
      );

      // tier
      newSubscriptions.forEach((i) => {
        const tier = tiers.find(
          (t) => t.classroomId === i.classroomId && t.tierId == i.tierId,
        );
        if (tier !== undefined) {
          SpaceStore.addSpaceMemebershipTierToMap(
            state,
            i.classroomId,
            i.tierId,
            new SpaceMembershipTier(tier),
          );
        } else {
          SpaceStore.addSpaceMemebershipTierToMap(
            state,
            i.classroomId,
            i.tierId,
            null,
          );
        }
      });

      return { ...state };
    });
    return { shouldFetchMore: !!newCursor && subscriptions.length === 0 };
  };

  const fetchTierSubscriptions: SpaceStoreAction['fetchTierSubscriptions'] =
    async (cursor, options) => {
      const fieldName = SpaceMembershipTierSubscriptionListType.ActiveUser;
      // Fetch from server
      const { ok, data } = await restGetSpaceMembershipTierSubscriptions(
        { cursor: cursor },
        options,
      );
      if (ok) {
        return handleFetchTierSubscriptionsResult(
          fieldName,
          cursor,
          data.classroomMembershipTierSubscriptions,
          data.classroomMembershipTiers,
          // data.classrooms,
          data.cursor ?? null,
        );
      }
      return { shouldFetchMore: false };
    };

  const getSpaceMemebershipTier: SpaceStoreAction['getSpaceMemebershipTier'] = (
    classroomId,
    tierId,
  ) => SpaceStore.getSpaceMemebershipTier(get(), classroomId, tierId);

  const addSpaceToField: SpaceStoreAction['addSpaceToField'] = (
    fieldName,
    classroom,
  ) => {
    set((state) => {
      const space = new Space(classroom);
      SpaceStore.pushSpaces(state, fieldName, space);
      return { ...state };
    });
  };

  const onTierInventoryPrune: SpaceStoreAction['onTierInventoryPrune'] = (
    inventory,
  ) => {
    set((state) => {
      SpaceStore.deleteSpaceMemebershipTierMapItem(
        state,
        SpaceMembershipTierMapType.ActiveUser,
        `${inventory.classroomId}_${inventory.tierId}`,
      );
      return { ...state };
    });
  };

  const addAlbumToField: SpaceStoreAction['addAlbumToField'] = (
    fieldName,
    album,
  ) => {
    set((state) => {
      const newAlbum = new Album(album);
      SpaceStore.setAlbums(state, fieldName, newAlbum);
      return { ...state };
    });
  };

  const getAlbums: SpaceStoreAction['getAlbums'] = (fieldName) =>
    SpaceStore.getAlbums(get(), fieldName);

  const getAlbum: SpaceStoreAction['getAlbum'] = (gameSetId) =>
    SpaceStore.getAlbum(get(), gameSetId);

  const fetchAlbums: SpaceStoreAction['fetchAlbums'] = async (
    { type, classroomId, partitionId, webBoosterType, withAlbumSummaries },
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetAlbums(
      { classroomId },
      { partitionId, webBoosterType, cursor, limit: 20 },
      options,
    );

    if (ok) {
      const albums = data.gameSets.map((o) => new Album(o));
      const newCursor = data.cursor ?? null;

      set((state) => {
        if (cursor === undefined) {
          SpaceStore.setAlbums(state, type, ...albums);
        } else {
          SpaceStore.pushAlbums(state, type, ...albums);
        }
        SpaceStore.setAlbumCursor(state, type, newCursor);

        return { ...state };
      });

      // Fetch album summaries if required
      if (withAlbumSummaries && albums.length > 0) {
        const gameSetIds = albums.map((album) => album.gameSetId);

        useRecordStore
          .getState()
          .fetchAlbumSummaries(
            { gameSetIds: gameSetIds },
            { headers: { 'cache-control': 'no-cache' } },
          )
          .catch((error) => {
            console.error('Failed to fetch album summaries:', error);
          });
      }
    }
    return {
      shouldFetchMore: !!data?.cursor && data?.gameSets.length === 0,
    };
  };

  const fetchRecentAlbums: SpaceStoreAction['fetchRecentAlbums'] = async (
    { fieldName, classroomId, withAlbumSummaries },
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetRecentAlbums(
      { classroomId, cursor, limit: 20, withProfiles: true },
      options,
    );

    if (ok) {
      const albums = data.gameSets.map((o) => new Album(o));
      const newCursor = data.cursor ?? null;

      set((state) => {
        if (cursor === undefined) {
          SpaceStore.setAlbums(
            state,
            fieldName ?? AlbumListType.Recent,
            ...albums,
          );
        } else {
          SpaceStore.pushAlbums(
            state,
            fieldName ?? AlbumListType.Recent,
            ...albums,
          );
        }
        SpaceStore.setAlbumCursor(
          state,
          fieldName ?? AlbumListType.Recent,
          newCursor,
        );

        return { ...state };
      });

      handleFetchSpacesResult(
        SpaceListType.Album,
        cursor,
        data.classrooms,
        data.profiles,
        data.cursor,
      );

      // Fetch album summaries if required
      if (withAlbumSummaries && albums.length > 0) {
        const gameSetIds = albums.map((album) => album.gameSetId);

        useRecordStore
          .getState()
          .fetchAlbumSummaries(
            { gameSetIds: gameSetIds },
            { headers: { 'cache-control': 'no-cache' } },
          )
          .catch((error) => {
            console.error('Failed to fetch album summaries:', error);
          });
      }
    }
    return {
      shouldFetchMore: !!data?.cursor && data?.gameSets.length === 0,
    };
  };

  const fetchAlbum: SpaceStoreAction['fetchAlbum'] = async (
    { fieldName = AlbumListType.Normal, classroomId, gameSetId, scope },
    options,
  ) => {
    const { ok, data } = await restGetAlbum(
      { classroomId, gameSetId },
      { scope },
      options,
    );
    if (ok) {
      const album = data.gameSet;
      if (album !== null) {
        set((state) => {
          addAlbumToField(fieldName, album);
          return { ...state };
        });
      }
    }
  };

  const fetchAlbumsByIds: SpaceStoreAction['fetchAlbumsByIds'] = async (
    { fieldName = AlbumListType.Normal, classroomId, gameSetIds, scopes },
    options,
  ) => {
    const fetchGameSetIds = gameSetIds.map((gameSetId, index) => ({
      gameSetId,
      scope: scopes?.[index],
    }));
    const resultGameSet: Album[] = [];
    await each(
      fetchGameSetIds,
      asyncify(
        async ({ gameSetId, scope }: { gameSetId: string; scope?: string }) => {
          const { ok, data } = await restGetAlbum(
            { classroomId, gameSetId },
            { scope },
            options,
          );
          if (ok) {
            const album = data.gameSet;
            if (album !== null) {
              const newAlbum = new Album(album);
              resultGameSet.push(newAlbum);
            }
          }
        },
      ),
    );
    set((state) => {
      SpaceStore.pushAlbums(state, fieldName, ...resultGameSet);
      return { ...state };
    });
  };

  const updateAlbum: SpaceStoreAction['updateAlbum'] = async (
    classroomId,
    gameSetId,
    params,
    options,
  ) => {
    const { ok, data } = await restUpdateAlbum(
      { classroomId, gameSetId },
      params,
      options,
    );
    if (ok) {
      const album = data.gameSet;
      if (album !== null) {
        set((state) => {
          SpaceStore.setAlbum(state, new Album(album));
          return { ...state };
        });
      }
    }
  };

  const deleteAlbum: SpaceStoreAction['deleteAlbum'] = async (
    classroomId,
    gameSetId,
    options,
  ) => {
    const { ok } = await restDeleteAlbum({ classroomId, gameSetId }, options);
    if (ok) {
      set((state) => {
        SpaceStore.deleteAlbum(state, gameSetId);
        return { ...state };
      });
      return true;
    }

    return false;
  };

  const addMembershipToField: SpaceStoreAction['addMembershipToField'] = (
    fieldName,
    membership,
  ) => {
    set((state) => {
      const newMembership = new SpaceMembership(membership);
      SpaceStore.setMemberships(state, fieldName, newMembership);
      return { ...state };
    });
  };
  const getMembership: SpaceStoreAction['getMembership'] = (
    classroomId,
    userId,
  ) => SpaceStore.getMembership(get(), classroomId, userId);

  const getSpaceMembership: SpaceStoreAction['getSpaceMembership'] = (
    classroomId,
    userId,
  ) => SpaceStore.getSpaceMembership(get(), classroomId, userId);

  const getActiveUserMembership: SpaceStoreAction['getActiveUserMembership'] = (
    classroomId,
  ) => SpaceStore.getActiveUserMembership(get(), classroomId);

  // const changeUserSpaceMembership: SpaceStoreAction['changeUserSpaceMembership'] =
  //   async (classroomId, body) => {
  // try {
  //   await useUploadStore
  //     .getState()
  //     .pushChangeUserSpaceMembershipTask({ classroomId }, body).promise;
  //   return ChangeSpaceMembershipResult.Success;
  // } catch (err) {
  //   console.error('changeUserSpaceMembership', (err as Error).message);
  //   return ChangeSpaceMembershipResult.Error;
  // }
  // };

  const changeUserSpaceMembership: SpaceStoreAction['changeUserSpaceMembership'] =
    async (classroomId, body) => {
      const response = await restChangeSpaceMembership(
        { classroomId: classroomId },
        body,
      );
      const { ok } = response;
      if (ok) {
        // ok
      } else {
        console.error(
          'Fail to change UserSpaceMembership',
          response.error.message,
        );
      }
      return response;
    };
  const joinTeamForLeaderboard: SpaceStoreAction['joinTeamForLeaderboard'] =
    async (classroomId, leaderboardId, body) => {
      const response = await restJoinTeamForLeaderboard(
        { classroomId: classroomId, leaderboardId: leaderboardId },
        body,
      );
      const { ok } = response;
      if (ok) {
        // ok
      } else {
        console.error(
          'Fail to change UserSpaceMembership',
          response.error.message,
        );
      }
      return response;
    };

  const seeSpace: SpaceStoreAction['seeSpace'] = async (classroomId) => {
    const response = await restSeeSpace({ classroomId: classroomId });
    const { ok } = response;
    if (ok) {
      const membership = new SpaceMembership(response.data.membership);
      return membership;
    } else {
      console.error('Fail to see Space', response.error.message);
    }
    return undefined;
  };

  const addTeamToField: SpaceStoreAction['addTeamToField'] = (
    fieldName,
    team,
  ) => {
    set((state) => {
      const newTeam = new SpaceTeam(team);
      SpaceStore.setTeams(state, fieldName, newTeam);
      return { ...state };
    });
  };

  const getTeam: SpaceStoreAction['getTeam'] = (teamId) =>
    SpaceStore.getTeam(get(), teamId);

  const getLeaderboard: SpaceStoreAction['getLeaderboard'] = (leaderboardId) =>
    SpaceStore.getLeaderboard(get(), leaderboardId);
  const addLeaderboardToField: SpaceStoreAction['addLeaderboardToField'] = (
    fieldName,
    leaderboard,
  ) => {
    set((state) => {
      const newLeaderboard = new Leaderboard(leaderboard);
      SpaceStore.setLeaderboards(state, fieldName, newLeaderboard);
      return { ...state };
    });
  };

  const getMultiplayerGame: SpaceStoreAction['getMultiplayerGame'] = (
    multiplayerGameId,
  ) => SpaceStore.getMultiplayerGame(get(), multiplayerGameId);

  const addMultiplayerGameToField: SpaceStoreAction['addMultiplayerGameToField'] =
    (fieldName, multiplayerGame) => {
      set((state) => {
        const newMultiplayerGame = new MultiplayerGame(multiplayerGame);
        SpaceStore.setMultiplayerGames(state, fieldName, newMultiplayerGame);
        return { ...state };
      });
    };

  const getGame: SpaceStoreAction['getGame'] = (gameId) =>
    SpaceStore.getGame(get(), gameId);

  const addGameToField: SpaceStoreAction['addGameToField'] = (
    fieldName,
    game,
  ) => {
    set((state) => {
      const newGame = jsonToGame(game);
      SpaceStore.setGames(state, fieldName, newGame);
      return { ...state };
    });
  };

  const getMultiplayerGameSession: SpaceStoreAction['getMultiplayerGameSession'] =
    (multiplayerGameSessionId) =>
      SpaceStore.getMultiplayerGameSession(get(), multiplayerGameSessionId);

  const addMultiplayerGameSessionToField: SpaceStoreAction['addMultiplayerGameSessionToField'] =
    (fieldName, multiplayerGameSession) => {
      set((state) => {
        const newMultiplayerGameSession = new MultiplayerGameSession(
          multiplayerGameSession,
        );
        SpaceStore.setMultiplayerGameSession(
          state,
          fieldName,
          newMultiplayerGameSession,
        );
        return { ...state };
      });
    };

  const getPerformanceReport: SpaceStoreAction['getPerformanceReport'] = async (
    { classroomId, webBoosterType, partitionId, from, to },
    options,
  ) => {
    const response = await restGetSpacePerformanceReport(
      { classroomId },
      { webBoosterType, partitionId, from, to },
      options,
    );
    const { ok } = response;
    if (ok) {
      return response.data;
      // ok
    } else {
      console.error(
        'Fail to change UserSpaceMembership',
        response.error.message,
      );
    }
    return undefined;
  };

  const fetchPerformanceReportGames: SpaceStoreAction['fetchPerformanceReportGames'] =
    async (
      { fieldName, classroomId, partitionId, webBoosterType, from, to },
      cursor,
      options,
    ) => {
      const { ok, data } = await restGetSpacePerformanceReportGame(
        { classroomId },
        { webBoosterType, partitionId, from, to, cursor, limit: 20 },
        options,
      );

      if (ok) {
        const performanceGames = data.games.map((g) => new PerformanceGame(g));
        const newCursor = data.cursor ?? null;

        set((state) => {
          if (cursor === undefined) {
            SpaceStore.setPerformanceGames(
              state,
              fieldName,
              ...performanceGames,
            );
          } else {
            SpaceStore.pushPerformanceGames(
              state,
              fieldName,
              ...performanceGames,
            );
          }
          SpaceStore.setPerformanceGameCursor(state, fieldName, newCursor);

          return { ...state };
        });
      }
      return {
        shouldFetchMore: !!data?.cursor && data?.games.length === 0,
      };
    };

  const addPerformanceGamesToField: SpaceStoreAction['addPerformanceGamesToField'] =
    (fieldName, performanceGames) => {
      set((state) => {
        const newPerformanceGames = performanceGames.map(
          (g) => new PerformanceGame(g),
        );
        SpaceStore.setPerformanceGames(
          state,
          fieldName,
          ...newPerformanceGames,
        );
        return { ...state };
      });
    };

  const getPerformanceReportGames: SpaceStoreAction['getPerformanceReportGames'] =
    (fieldName) => SpaceStore.getPerformanceGames(get(), fieldName);

  const onAlbumPrune: SpaceStoreAction['onAlbumPrune'] = (album) => {
    useRecordStore.getState().clearAlbumSummary(album.id());
  };

  return {
    initialize,
    shutdown,
    spaces: new SpaceRepository(), // only rehydrated one listen to prune action?
    spaceMembershipTierInventories:
      new SpaceMembershipTierInventoryRepository(),
    spaceMembershipTierSubscriptions:
      new SpaceMembershipTierSubscriptionRepository(),
    spaceMembershipTiers: new SpaceMembershipTierRepository(),
    albums: new AlbumRepository(),
    spaceMemberships: new SpaceMembershipRepository(),
    teams: new SpaceTeamRepository(),
    leaderboards: new SpaceLeaderboardRepository(),
    games: new GameRepository(),
    multiplayerGames: new MultiplayerGameRepository(),
    multiplayerGameSessions: new MultiplayerGameSessionRepository(),
    performanceGames: new PerformanceGameRepository(),
    getSpace,
    getSpaces,
    resetSpacesList,
    handleFetchSpacesResult,
    addSpaceToField,
    fetchManagingSpaces: lifecycle.ifReadyElseThrow(fetchManagingSpaces),
    fetchManagingSpacesByUserId: lifecycle.ifReadyElseThrow(
      fetchManagingSpacesByUserId,
    ),
    fetchSpace: lifecycle.ifReadyElseThrow(fetchSpace),
    searchSpaces: lifecycle.ifReadyElseThrow(searchSpaces),
    fetchHotSpaces: lifecycle.ifReadyElseThrow(fetchHotSpaces),
    fetchRecentSpaces: lifecycle.ifReadyElseThrow(fetchRecentSpaces),
    getTierInventories,
    fetchTierInventories: lifecycle.ifReadyElseThrow(fetchTierInventories),
    activateTierInventory,
    getTierSubscriptions,
    fetchTierSubscriptions,
    getSpaceMemebershipTier,
    onTierInventoryPrune,
    addAlbumToField,
    getAlbums,
    getAlbum,
    fetchAlbum,
    fetchAlbums,
    fetchAlbumsByIds,
    fetchRecentAlbums,
    updateAlbum,
    deleteAlbum,
    // membership
    addMembershipToField,
    getMembership,
    getActiveUserMembership,
    getSpaceMembership,
    changeUserSpaceMembership,
    seeSpace,
    //team
    addTeamToField,
    getTeam,
    // leaderboard
    getLeaderboard,
    addLeaderboardToField,
    joinTeamForLeaderboard,
    // multiplayer game
    getMultiplayerGame,
    addMultiplayerGameToField,
    // multiplayer game session
    getMultiplayerGameSession,
    addMultiplayerGameSessionToField,
    //game
    getGame,
    addGameToField,
    //performance report
    getPerformanceReport,
    fetchPerformanceReportGames,
    addPerformanceGamesToField,
    getPerformanceReportGames,
    // prune listener
    onAlbumPrune,
  };
};

const persistOptions: PersistOptions<Store, Partial<SpaceStoreState>> = {
  name: 'space-storage',
  // getStorage: () => AsyncStorage,
  deserialize: (str: string): StorageValue<Partial<SpaceStoreState>> => {
    try {
      const { state, version } = JSON.parse(
        str,
        dateNumberReviver,
      ) as StorageValue<SpaceStoreStateJson>;
      return {
        state: {
          spaces: new SpaceRepository(state.spaces),
          spaceMembershipTierInventories:
            new SpaceMembershipTierInventoryRepository(),
          spaceMembershipTiers: new SpaceMembershipTierRepository(),
          albums: new AlbumRepository(state.albums),
          spaceMemberships: new SpaceMembershipRepository(),
          teams: new SpaceTeamRepository(state.teams),
          leaderboards: new SpaceLeaderboardRepository(state.leaderboards),
          multiplayerGames: new MultiplayerGameRepository(
            state.multiplayerGames,
          ),
        },
        version,
      };
    } catch (err) {
      console.error('Fail to deserialize space-storage', str);
      console.error(err);
      return { state: {} };
    }
  },
  onRehydrateStorage: () => {
    return (state) => {
      if (state) {
        // state.spaces.onElementPrune = state.onSpacePrune;
        state.albums.onElementPrune = state.onAlbumPrune;
        state.spaceMembershipTierInventories.onElementPrune =
          state.onTierInventoryPrune;
      }
      lifecycle.finishHydration();
      // console.log('finish space hydration');
      // TODO: handle rehydrate error case by shutdown?
    };
  },
};

export const useSpaceStore = createWithEqualityFn<Store>()(
  persist(initializer, persistOptions),
  Object.is,
);
export const useSpaceStoreListField = createStoreListFieldHook(useSpaceStore);
export const useSpaceStoreMapField = createStoreMapFieldHook(useSpaceStore);
